import { darken, lighten } from '@material-ui/core/styles';
import { ThemeModes } from 'UI/theme/themeMode';

/**
 * Dark-mode tones for elements whose light colors are fixed (avatars, filled status chips, dark
 * chips, hover cards, header bands). Every helper returns the light value unchanged in light mode,
 * so light mode stays identical.
 */

/**
 * @param {Object} theme MUI theme
 * @returns {boolean} whether the theme is the dark palette
 */
export const isDarkTheme = theme => theme.palette.type === ThemeModes.Dark;

/**
 * Avatar with a pale background and a saturated text color. In dark mode the pale background
 * would glow on dark surfaces, so it becomes a deep shade of the text hue with lighter text.
 * @param {Object} theme MUI theme
 * @param {{ background: string, text: string }} light colors used in light mode
 * @returns {{ backgroundColor: string, color: string }}
 */
export const getAvatarTone = (theme, { background, text }) =>
  isDarkTheme(theme)
    ? { backgroundColor: darken(text, 0.6), color: lighten(text, 0.3) }
    : { backgroundColor: background, color: text };

/**
 * Background of a filled chip with white text. Light keeps its color; dark uses a deeper shade so
 * white text stays readable.
 * @param {Object} theme MUI theme
 * @param {string} color background used in light mode
 * @returns {string}
 */
export const getFilledChipBackground = (theme, color) => {
  if (!isDarkTheme(theme) || !color) return color;
  try {
    return darken(color, 0.45);
  } catch {
    return color;
  }
};

/**
 * Dark chips, hover cards and header rows (dark background, white text) are built from high grey
 * steps that invert in dark mode and would turn light behind white text. Keeps the exact light
 * color and uses a fixed dark surface in dark mode.
 * @param {Object} theme MUI theme from `makeStyles(theme => ...)` or `styled`
 * @param {string} lightColor background used in light mode, unchanged
 * @returns {string}
 */
export const getInverseSurface = (theme, lightColor) =>
  isDarkTheme(theme) ? theme.palette.grey[400] : lightColor;

/**
 * Background of card and table header bands: `grey[50]` in light, the raised `grey[300]` surface in
 * dark so the band separates from the card body.
 * @param {Object} theme MUI theme
 * @returns {string}
 */
export const getHeaderBandBackground = theme =>
  isDarkTheme(theme) ? theme.palette.grey[300] : theme.palette.grey[50];

const LIGHT_FILTER_FOOTER_BORDER = '#eff2f7';

/**
 * Color of standalone icons whose light color is a fixed pale grey (search bar magnifier, hover
 * card actions). Light keeps it; dark uses `action.active`, like the other app icons.
 * @param {Object} theme MUI theme
 * @param {string} lightColor icon color used in light mode, unchanged
 * @returns {string}
 */
export const getIconColor = (theme, lightColor) =>
  isDarkTheme(theme) ? theme.palette.action.active : lightColor;

/**
 * Default fill for single-color illustrations that have no fill of their own. Light returns
 * `undefined`, so the SVG keeps its original (black) default; dark uses the illustration color so
 * the drawing doesn't render black on dark surfaces.
 * @param {Object} theme MUI theme
 * @returns {(string|undefined)}
 */
export const getDarkIllustrationFill = theme =>
  isDarkTheme(theme) ? theme.palette.customColors.illustration : undefined;

/**
 * Dark-only styles for a container whose SVG icons have no fill of their own. SVG paints those
 * black, which disappears on dark surfaces. Light adds nothing, so light icons stay as they are.
 * @param {Object} theme MUI theme
 * @param {string} [color='currentColor'] fill for the unfilled icons in dark mode
 * @returns {Object} style object to spread into the container styles
 */
export const getDarkUnfilledIconStyles = (theme, color = 'currentColor') =>
  isDarkTheme(theme) ? { '& svg:not([fill])': { fill: color } } : {};

/**
 * Top border color of filter panel footers: the existing light color, the palette divider in dark.
 * @param {Object} theme MUI theme
 * @returns {string}
 */
export const getFilterFooterBorderColor = theme =>
  isDarkTheme(theme) ? theme.palette.divider : LIGHT_FILTER_FOOTER_BORDER;
